import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Globe,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  Info
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface SmsCountry {
  country_code: string;
  name: string;
  registration_type: string;
  registration_status: 'completed' | 'pending' | 'not_started';
  enabled: boolean;
  updated_at?: number;
  updated_by?: string;
}

const registrationStatusConfig = {
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    color: 'text-green-600',
    badge: 'bg-green-100 text-green-800',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-yellow-600',
    badge: 'bg-yellow-100 text-yellow-800',
  },
  not_started: {
    label: 'Not Started',
    icon: XCircle,
    color: 'text-gray-500',
    badge: 'bg-gray-100 text-gray-800',
  },
};

const registrationTypeLabels: Record<string, string> = {
  '10DLC': '10DLC (A2P)',
  'Sender ID': 'Sender ID',
  'DLT': 'DLT Registration',
  'none_required': 'None Required',
};

export function SmsCountrySettings() {
  const [countries, setCountries] = useState<SmsCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    country: SmsCountry | null;
    newEnabled: boolean;
  }>({ open: false, country: null, newEnabled: false });

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{ countries: SmsCountry[] }>('/admin/sms/countries');
      setCountries(response.countries || []);
    } catch (err) {
      console.error('Failed to load SMS countries:', err);
      setError('Failed to load SMS country settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (country: SmsCountry, newEnabled: boolean) => {
    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      country,
      newEnabled,
    });
  };

  const confirmToggle = async () => {
    const { country, newEnabled } = confirmDialog;
    if (!country) return;

    setConfirmDialog({ open: false, country: null, newEnabled: false });
    setUpdating(country.country_code);
    setError(null);

    try {
      const response = await apiClient.put<{ ok: boolean; error?: string }>(
        `/admin/sms/countries/${encodeURIComponent(country.country_code)}`,
        { enabled: newEnabled }
      );

      if (response.ok) {
        // Update local state
        setCountries(prev =>
          prev.map(c =>
            c.country_code === country.country_code
              ? { ...c, enabled: newEnabled, updated_at: Date.now() / 1000 }
              : c
          )
        );
      } else {
        setError(response.error || 'Failed to update country setting');
      }
    } catch (err: any) {
      console.error('Failed to update country:', err);
      // Try to extract error message from response
      const errorMessage = err.message?.includes('pending')
        ? 'Cannot enable country: registration is pending'
        : err.message?.includes('not_started')
        ? 'Cannot enable country: registration has not started'
        : 'Failed to update country setting. Please try again.';
      setError(errorMessage);
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (timestamp: number | undefined) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const enabledCount = countries.filter(c => c.enabled).length;
  const completedCount = countries.filter(c => c.registration_status === 'completed').length;
  const pendingCount = countries.filter(c => c.registration_status === 'pending').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">SMS Country Settings</h1>
        <p className="text-muted-foreground">
          Manage which countries can receive SMS messages from your platform
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Countries</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{countries.length}</div>
            <p className="text-xs text-muted-foreground">Available destinations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enabled</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{enabledCount}</div>
            <p className="text-xs text-muted-foreground">Active destinations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Registration Complete</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{completedCount}</div>
            <p className="text-xs text-muted-foreground">Ready to enable</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Registration</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting approval</p>
          </CardContent>
        </Card>
      </div>

      {/* Info Alert */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="flex items-start gap-3 pt-4">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Note:</strong> Countries can only be enabled after their carrier registration is complete.
            Registration status reflects the current state of carrier-specific requirements (10DLC, DLT, Sender ID, etc.).
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </CardContent>
        </Card>
      )}

      {/* Countries Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                SMS Destination Countries
              </CardTitle>
              <CardDescription>
                Toggle countries on or off to control where SMS messages can be sent
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadCountries}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading countries...</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Code</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Registration Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {countries.map(country => {
                  const statusConfig = registrationStatusConfig[country.registration_status];
                  const StatusIcon = statusConfig.icon;
                  const canEnable = country.registration_status === 'completed';

                  return (
                    <TableRow key={country.country_code}>
                      <TableCell className="font-mono font-medium">
                        {country.country_code}
                      </TableCell>
                      <TableCell className="font-medium">{country.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {registrationTypeLabels[country.registration_type] || country.registration_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.badge}>
                          <StatusIcon className={`h-3 w-3 mr-1 ${statusConfig.color}`} />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={country.enabled}
                            onCheckedChange={(checked) => handleToggle(country, checked)}
                            disabled={!canEnable || updating === country.country_code}
                          />
                          {updating === country.country_code && (
                            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {formatDate(country.updated_at)}
                        {country.updated_by && (
                          <div className="text-xs">by {country.updated_by}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, country: null, newEnabled: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.newEnabled ? 'Enable' : 'Disable'} SMS for {confirmDialog.country?.name}?
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.newEnabled ? (
                <>
                  Enabling SMS for <strong>{confirmDialog.country?.name} ({confirmDialog.country?.country_code})</strong> will
                  allow customers with phone numbers from this country to receive SMS notifications.
                </>
              ) : (
                <>
                  Disabling SMS for <strong>{confirmDialog.country?.name} ({confirmDialog.country?.country_code})</strong> will
                  prevent customers with phone numbers from this country from receiving SMS notifications.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, country: null, newEnabled: false })}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmToggle}
              variant={confirmDialog.newEnabled ? 'default' : 'destructive'}
            >
              {confirmDialog.newEnabled ? 'Enable' : 'Disable'} SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SmsCountrySettings;
